// Buyer location & display preferences — country, city and display currency,
// picked from the location modal and persisted in localStorage. Language is
// owned by i18next (its own localStorage key), so it's not duplicated here.
//
// The full `Currency` object is persisted (not just its code) so price
// formatting stays synchronous and works before the geo endpoints resolve —
// `useCurrency()` is read by every price on screen via `useFormatPrice()`.
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { safeLocalStorage } from "./safe-storage";

import type { Currency } from "@/api";
import {
	EMPTY_FEED_FILTERS,
	type FeedFilters,
} from "@/components/feed/feed-settings-panel";
import { DEFAULT_DEPARTMENT, DEFAULT_HOME_SEGMENT, type Department } from "@/lib/home-feed";
import type { FeedSegment } from "@/api";
import { BASE_CURRENCY } from "@/lib/money";

type PreferencesState = {
	countryCode: string;
	countryName: string;
	city: string;
	currency: Currency;
	// Раздел шапки («Одежда» / «Красота» / «Дом и интерьер») — корни дерева
	// категорий API (lib/category-tree.ts). Это рамка просмотра, а не фасет
	// сервера: сужает ленту главной и каталог корнями раздела. Хранится, чтобы
	// выбор переживал перезагрузку; ключ, которого нет в дереве, — «Одежда».
	department: Department;
	// Аудитория («Женщинам / Мужчинам / Детям»). Живёт рядом с department, а не в
	// состоянии главной: по сегменту делятся и категории, и идеи, и подборки —
	// страница идей обязана открыться в том же сегменте, в каком её покинули.
	segment: FeedSegment;
	// Фильтры «Моей ленты» — то, что покупатель собрал кнопкой «Настроить».
	// Живут рядом с прочими предпочтениями витрины и переживают перезагрузку;
	// для вошедшего покупателя лента синхронизируется с сохранённой на сервере.
	feedFilters: FeedFilters;
	// Город выбран явно ИЛИ определён по гео — чтобы не переопределять выбор
	// пользователя автоопределением. По умолчанию false → при первом входе
	// пробуем определить город (иначе остаётся дефолт «Москва»).
	cityChosen: boolean;
	// Геолокацию спрашиваем один раз за всё время (успех/отказ), чтобы не
	// дёргать системный диалог на каждом заходе.
	geoPrompted: boolean;
	setLocation: (country: { code: string; name: string }, city: string) => void;
	setCurrency: (currency: Currency) => void;
	setDepartment: (department: Department) => void;
	setSegment: (segment: FeedSegment) => void;
	setFeedFilters: (filters: FeedFilters) => void;
	setGeoPrompted: () => void;
};

export const usePreferences = create<PreferencesState>()(
	persist(
		(set) => ({
			// Defaults to the primary market (matches the ru default language).
			countryCode: "RU",
			countryName: "Россия",
			city: "Москва",
			currency: BASE_CURRENCY,
			department: DEFAULT_DEPARTMENT,
			segment: DEFAULT_HOME_SEGMENT,
			feedFilters: EMPTY_FEED_FILTERS,
			cityChosen: false,
			geoPrompted: false,
			setLocation: (country, city) =>
				set({ countryCode: country.code, countryName: country.name, city, cityChosen: true }),
			setCurrency: (currency) => set({ currency }),
			setDepartment: (department) => set({ department }),
			setSegment: (segment) => set({ segment }),
			setFeedFilters: (feedFilters) => set({ feedFilters }),
			setGeoPrompted: () => set({ geoPrompted: true }),
		}),
		{ name: "osmix-prefs", storage: createJSONStorage(() => safeLocalStorage) },
	),
);

// Narrow selector used by price formatting throughout the app.
export const useCurrency = (): Currency => usePreferences((s) => s.currency);

// The active header department («Одежда» / «Дом»).
export const useDepartment = (): Department => usePreferences((s) => s.department);

// Активная аудитория («Женщинам / Мужчинам / Детям»).
export const useSegment = (): FeedSegment => usePreferences((s) => s.segment);

// Фильтры «Моей ленты».
export const useFeedFilters = (): FeedFilters => usePreferences((s) => s.feedFilters);
